<template>
  <div
    class="w-full rounded-md select-none overflow-hidden border border-[#CCCCCC]/50"
  >
    <!-- Headers -->
    <div
      class="w-full h-10 bg-[#FAFAFA] font-primary font-medium text-[14px] border-b border-[#DFDFDF] flex items-center px-5"
    >
      <div class="w-1/2 tablet-h:w-1/5">Date</div>
      <div class="hidden tablet-h:block w-1/5">Description</div>
      <div class="hidden tablet-h:block w-1/5">Total</div>
      <div class="w-1/2 text-right tablet-h:text-left tablet-h:w-1/5">
        Status
      </div>
      <div class="hidden tablet-h:block w-1/5" />
    </div>

    <!-- Skeleton -->
    <template v-if="showSkeleton">
      <div
        v-for="n in 3"
        :key="n"
        class="w-full h-[50px] border-b last:border-none border-[#DFDFDF] flex items-center justify-between px-5"
      >
        <div class="w-1/5"><Skeleton class="w-[80px] h-4" /></div>
        <div class="w-1/5"><Skeleton class="w-[80px] h-4" /></div>
        <div class="w-1/5"><Skeleton class="w-[80px] h-4" /></div>
        <div class="w-1/5"><Skeleton class="w-[80px] h-4" /></div>
        <div class="w-1/5" />
      </div>
    </template>

    <div
      v-else-if="!props.invoices?.length"
      class="font-secondary text-[14px] h-[100px] flex items-center justify-center"
    >
      You haven't made any payments
    </div>

    <!-- Invoice rows -->
    <template v-else>
      <div
        v-for="invoice in props.invoices"
        :key="invoice.id"
        class="font-secondary text-[14px] tablet-h:h-[50px] px-5 pt-[14px] pb-[18px] tablet-h:py-0 border-b last:border-none border-[#DFDFDF] flex flex-col justify-center"
      >
        <div
          class="flex justify-between tablet-h:[justify-content:initial] items-start tablet-h:items-center"
        >
          <div class="w-1/2 tablet-h:w-1/5">
            <div class="w-fit">
              <div
                v-if="!isInvoiceRequestProcessing(invoice)"
                class="flex items-center cursor-pointer group"
                @click="handleClick(invoice)"
              >
                <div class="mr-1.5 text-green-700 group-hover:text-green-600">
                  {{ formatInvoiceDate(invoice.date) }}
                </div>
                <Icon
                  :size="13"
                  name="External"
                  class="stroke-green-700 group-hover:stroke-green-600"
                />
              </div>
              <div v-else class="flex items-center">
                <Spinner :size="16" class="mr-2" />
                <div class="opacity-50">Loading invoice</div>
              </div>
            </div>
            <div class="mt-1.5 text-black flex items-center tablet-h:hidden">
              <div class="mr-2 whitespace-nowrap">
                {{ invoice.description }}
              </div>
              <div>{{ formatInvoiceTotal(invoice) }}</div>
            </div>
          </div>
          <div class="hidden tablet-h:block w-1/5 leading-[16px]">
            {{ invoice.description }}
          </div>
          <div class="hidden tablet-h:block w-1/5 leading-[16px]">
            {{ formatInvoiceTotal(invoice) }}
          </div>
          <div
            class="w-1/2 tablet-h:w-1/5 flex items-center justify-end tablet-h:justify-start"
          >
            <Pill
              :success="!isInvoiceOverdue(invoice)"
              :danger="isInvoiceOverdue(invoice)"
              class="flex items-center"
            >
              <Icon
                v-if="isInvoicePaid(invoice) || isInvoiceExpired(invoice)"
                :size="10"
                name="Check"
                class="stroke-green-900 mr-[3.5px]"
              />
              <Icon
                v-else
                :size="10"
                name="Alert"
                class="stroke-red-700 mr-[3.5px]"
              />
              {{ getInvoiceText(invoice) }}
            </Pill>
          </div>
          <div
            v-if="shouldShowUpdateBilling(invoice)"
            class="hidden tablet-h:flex w-1/5 justify-end"
          >
            <UiButton :disabled="disableButton" primary @click="handlePayClick">
              Pay balance
            </UiButton>
          </div>
        </div>
        <UiButton
          v-if="shouldShowUpdateBilling(invoice)"
          class="w-full! mt-[15px] tablet-h:hidden"
          :disabled="disableButton"
          primary
          @click="handlePayClick"
        >
          Pay balance
        </UiButton>
      </div>
    </template>

    <!-- View more -->
    <div
      v-if="showMoreInvoicesButton"
      class="w-full h-10 tablet-h:h-[30px] font-primary flex items-center justify-center text-[14px] font-medium"
      :class="viewMoreClasses"
      @click="handleViewMoreClick"
    >
      <Spinner v-if="viewMoreLoading" :size="12" class="mr-2" />
      <template v-else> View more billing history </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  InvoicePdfDocument,
  InvoicePdfStatusEnum,
  InvoiceStatus,
  UserPermission,
  type Invoice,
  type InvoicePdfQuery,
  type InvoicePdfQueryVariables,
  type InvoicePdfResult,
} from '@/shared/graphql/legacy/generated'
import { Pill, Skeleton, UiButton, Icon } from '@dacr/ui'
import { computed, ref, watch } from 'vue'
import { formatTimestamp } from '@/shared/utils/datetime'
import { useUserStore } from '@/shared/stores/user'
import { formatPrice } from '@/shared/utils/money'
import { apolloClient } from '@/shared/graphql/client'
import { delay } from '@/shared/utils/delay'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'
import Spinner from '@/shared/components/Spinner.vue'
import { useBillingStore } from '@/features/billing/store'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'

const props = defineProps<{
  invoices?: Invoice[] | null
  hasMore: boolean
}>()

const emit = defineEmits(['fetchMore'])

const billingStore = useBillingStore()
const dialog = useDialog()
const userStore = useUserStore()
const webviewStore = useWebviewStore()

const invoiceIdToQueryResponse = ref<Record<string, InvoicePdfResult>>({})
const viewMoreLoading = ref(false)

watch(props, () => {
  if (viewMoreLoading.value) {
    viewMoreLoading.value = false
  }
})

const showSkeleton = computed(
  () => props.invoices === null || props.invoices === undefined
)

const disableButton = computed(
  () => !userStore.user?.permissions.includes(UserPermission.Payments)
)

const user = computed(() => userStore.user)

const formatInvoiceDate = (timestamp: number) => {
  if (!user.value) return
  return formatTimestamp(timestamp, { formatString: user.value.dateFormat })
}

const formatInvoiceTotal = (invoice: Invoice) => {
  return formatPrice({
    amount: invoice.total / 100,
    currency: invoice.currency,
  })
}

const isInvoicePaid = (invoice: Invoice) =>
  invoice.status === InvoiceStatus.Paid

const isInvoiceExpired = (invoice: Invoice) =>
  invoice.status === InvoiceStatus.Expired

const isInvoiceOverdue = (invoice: Invoice) =>
  invoice.status === InvoiceStatus.Overdue

const showMoreInvoicesButton = computed(
  () => props.invoices !== undefined && props.invoices !== null && props.hasMore
)

const handleViewMoreClick = () => {
  viewMoreLoading.value = true
  emit('fetchMore')
}

const shouldShowUpdateBilling = (invoice: Invoice) => {
  const index = (props.invoices || []).findIndex((inv) => inv.id === invoice.id)
  return !isInvoicePaid(invoice) && !isInvoiceExpired(invoice) && index === 0
}

const isInvoiceRequestProcessing = (invoice: Invoice) => {
  return (
    invoiceIdToQueryResponse.value[invoice.id] &&
    invoiceIdToQueryResponse.value[invoice.id].status ===
      InvoicePdfStatusEnum.Processing
  )
}

const handlePayClick = () => {
  if (!billingStore.canUpdateSubscription && webviewStore.isActive) {
    return dialog.open(Dialogs.CantPay)
  }

  dialog.open(Dialogs.Pay, {
    events: {
      success() {
        dialog.open(Dialogs.Success, {
          props: { title: 'Payment successful' },
          events: {
            success() {
              dialog.close()
            },
          },
        })
      },
    },
  })
}

const viewMoreClasses = computed(() => ({
  'cursor-pointer': !viewMoreLoading.value,
  'pointer-events-none': viewMoreLoading.value,
}))

const getInvoiceText = (invoice: Invoice) => {
  if (invoice.status === InvoiceStatus.Paid) return 'Paid'
  if (invoice.status === InvoiceStatus.Expired) return 'Paid late'
  return 'Payment overdue'
}

const handleClick = async (invoice: Invoice) => {
  if (invoiceIdToQueryResponse.value[invoice.id]?.url) {
    const url = invoiceIdToQueryResponse.value[invoice.id].url as string
    if (webviewStore.isActive) {
      webviewStore.postMessage({ type: NativeActionsEnum.LinkOpen, data: url })
    } else {
      window.open(url, '_blank')
    }
    return
  }

  invoiceIdToQueryResponse.value[invoice.id] = {
    __typename: 'InvoicePdfResult',
    url: '',
    status: InvoicePdfStatusEnum.Processing,
  }

  do {
    const response = await apolloClient.query<
      InvoicePdfQuery,
      InvoicePdfQueryVariables
    >({
      query: InvoicePdfDocument,
      variables: { invoiceId: invoice.id },
      fetchPolicy: 'network-only',
    })

    if (
      response.data.invoicePdf.status === InvoicePdfStatusEnum.Finished &&
      response.data.invoicePdf.url
    ) {
      invoiceIdToQueryResponse.value[invoice.id] = {
        __typename: 'InvoicePdfResult',
        url: response.data.invoicePdf.url,
        status: InvoicePdfStatusEnum.Finished,
      }

      const pdfUrl = invoiceIdToQueryResponse.value[invoice.id].url as string
      if (webviewStore.isActive) {
        webviewStore.postMessage({
          type: NativeActionsEnum.LinkOpen,
          data: pdfUrl,
        })
      } else {
        setTimeout(() => {
          window.open(pdfUrl, '_blank')
        })
      }
      return
    } else {
      await delay(3000)
    }
  } while (
    invoiceIdToQueryResponse.value[invoice.id].status ===
    InvoicePdfStatusEnum.Processing
  )
}
</script>
